import * as Sentry from '@sentry/react';
import { createBeforeSend } from '@lynrx/shared';
import packageJson from '../../package.json';

type Environment = 'development' | 'staging' | 'production';
type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
type SentryBeforeSend = NonNullable<SentryInitOptions['beforeSend']>;

function readEnv(): Environment {
  const raw = (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE) as string;
  if (raw === 'production' || raw === 'staging') return raw;
  return 'development';
}

function readRelease(): string {
  const sha = import.meta.env.VITE_GIT_SHA;
  const version = packageJson.version;
  return sha ? `${version}+${sha}` : version;
}

/**
 * Initialise Sentry for apps/marketing. The marketing site has no
 * authenticated users — we never call setUser. PHI keys are still scrubbed
 * because a contact form might accidentally land in an error breadcrumb.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  Sentry.init({
    ...(dsn ? { dsn } : {}),
    environment: readEnv(),
    release: readRelease(),
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
    beforeSend: createBeforeSend({ dsn }) as unknown as SentryBeforeSend,
  });
}

export { Sentry };
